import { collection, doc, getDoc, getDocs, limit, query, setDoc, where } from 'firebase/firestore';
import { db } from './firebase';
import type { Listing } from '@shared/types';

/** Public browse cache — 1 Firestore read instead of ~140 listing docs. */
export const BROWSE_LISTINGS_DOC = 'browseListings';
export const BROWSE_LISTINGS_PATH = ['publicSettings', BROWSE_LISTINGS_DOC] as const;

export type BrowseListingsCache = {
  generatedAt?: string;
  count?: number;
  listings?: Listing[];
};

/** Convert Firestore Timestamps / nested values into JSON-safe plain data. */
export function toPlainListing(value: unknown): unknown {
  if (value == null) return value;
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  if (Array.isArray(value)) return value.map(toPlainListing);
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      // Drop undefined so Firestore setDoc stays happy
      if (v === undefined) continue;
      out[k] = toPlainListing(v);
    }
    return out;
  }
  return value;
}

export async function fetchBrowseListingsFromFirestore(): Promise<Listing[] | null> {
  const snap = await getDoc(doc(db, 'publicSettings', BROWSE_LISTINGS_DOC));
  if (!snap.exists()) return null;
  const data = snap.data() as BrowseListingsCache;
  if (!Array.isArray(data.listings) || data.listings.length === 0) return null;
  return data.listings as Listing[];
}

async function loadAllPublishedListings(): Promise<Listing[]> {
  const snap = await getDocs(
    query(collection(db, 'listings'), where('status', '==', 'published'), limit(500))
  );
  return snap.docs.map((d) => toPlainListing({ id: d.id, ...d.data() })) as Listing[];
}

/** Full rebuild of the public browse cache (admin / deploy scripts). */
export async function rebuildBrowseListingsCache(): Promise<Listing[]> {
  const listings = await loadAllPublishedListings();
  await setDoc(
    doc(db, 'publicSettings', BROWSE_LISTINGS_DOC),
    {
      generatedAt: new Date().toISOString(),
      count: listings.length,
      listings,
    },
    { merge: true }
  );
  return listings;
}

/**
 * After an admin save: upsert or remove one listing in the public browse cache.
 * Rebuilds the whole cache if it does not exist yet.
 */
export async function syncListingToBrowseCache(
  listingId: string,
  listing: Partial<Listing> & { status?: string }
): Promise<void> {
  const ref = doc(db, 'publicSettings', BROWSE_LISTINGS_DOC);
  const snap = await getDoc(ref);

  let listings: Listing[];
  if (snap.exists() && Array.isArray((snap.data() as BrowseListingsCache).listings)) {
    listings = [...((snap.data() as BrowseListingsCache).listings as Listing[])];
  } else {
    listings = await loadAllPublishedListings();
  }

  const status = listing.status || 'draft';
  if (status === 'published') {
    const plain = toPlainListing({ ...listing, id: listingId }) as Listing;
    const idx = listings.findIndex((l) => l.id === listingId);
    if (idx >= 0) listings[idx] = { ...listings[idx], ...plain, id: listingId };
    else listings.push({ ...plain, id: listingId });
  } else {
    listings = listings.filter((l) => l.id !== listingId);
  }

  await setDoc(
    ref,
    {
      generatedAt: new Date().toISOString(),
      count: listings.length,
      listings,
    },
    { merge: true }
  );
}
